import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

const AuthContext = createContext(null);

const AUTH_STORAGE_KEY = 'lottoAnalyticsAuth';

const API_LOGIN_URL =
  'https://www.my649.com/api/auth/login.php';

const API_ME_URL =
  'https://www.my649.com/api/auth/me.php';


/* =========================================================
   Auth Provider
========================================================= */

export function AuthProvider({ children }) {

  const [user, setUser] = useState(null);

  const [loading, setLoading] = useState(true);


  /* =======================================================
     Restore Login Session
  ======================================================= */

  useEffect(() => {

    const restoreSession = async () => {

      try {

        const savedAuth =
          localStorage.getItem(
            AUTH_STORAGE_KEY
          );


        /*
         * No saved authentication
         */

        if (!savedAuth) {

          setLoading(false);

          return;
        }


        /*
         * Parse saved authentication
         */

        let authData;

        try {

          authData =
            JSON.parse(savedAuth);

        } catch (error) {

          console.error(
            'Invalid saved authentication:',
            error
          );

          localStorage.removeItem(
            AUTH_STORAGE_KEY
          );

          setLoading(false);

          return;
        }


        const token =
          authData?.token;


        /*
         * Token is required
         */

        if (!token) {

          console.warn(
            'Saved authentication does not contain a token.'
          );

          localStorage.removeItem(
            AUTH_STORAGE_KEY
          );

          setUser(null);

          setLoading(false);

          return;
        }


        /* ---------------------------------------------------
           Verify Token with me.php
        --------------------------------------------------- */

        console.log(
          'Checking authentication session...'
        );


        const response =
          await fetch(
            API_ME_URL,
            {
              method: 'GET',

              headers: {
                'Accept':
                  'application/json',

                'Authorization':
                  `Bearer ${token}`,
              },

              credentials: 'include',
            }
          );


        let data = null;


        try {

          data =
            await response.json();

        } catch (error) {

          console.error(
            'Invalid me.php response:',
            error
          );

        }


        console.log(
          'me.php response:',
          response.status,
          data
        );


        /*
         * Session is valid
         */

        if (
          response.ok &&
          data?.success &&
          data?.user
        ) {

          setUser(
            data.user
          );


          /*
           * Update stored user information
           */

          const updatedAuth = {

            isAuthenticated: true,

            token: token,

            expiresAt:
              data.expiresAt ||
              authData.expiresAt ||
              null,

            user: data.user,

          };


          localStorage.setItem(
            AUTH_STORAGE_KEY,
            JSON.stringify(updatedAuth)
          );

        } else {

          /*
           * Token invalid / expired
           */

          console.warn(
            'Authentication session is invalid or expired.'
          );


          localStorage.removeItem(
            AUTH_STORAGE_KEY
          );

          setUser(null);

        }

      } catch (error) {

        console.error(
          'Failed to restore authentication:',
          error
        );


        /*
         * If server cannot be reached, do not
         * blindly trust the saved user.
         */

        localStorage.removeItem(
          AUTH_STORAGE_KEY
        );

        setUser(null);

      } finally {

        setLoading(false);

      }

    };


    restoreSession();

  }, []);


  /* =======================================================
     Login
  ======================================================= */

  const login = async (
    email,
    password
  ) => {

    setLoading(true);


    try {

      console.log(
        'Login API:',
        API_LOGIN_URL
      );


      /*
       * Call Login API
       */

      const response =
        await fetch(
          API_LOGIN_URL,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              'Accept':
                'application/json',
            },

            credentials: 'include',

            body: JSON.stringify({
              email,
              password,
            }),
          }
        );


      /* ---------------------------------------------------
         Read Response
      --------------------------------------------------- */

      let data;


      try {

        data =
          await response.json();

      } catch (jsonError) {

        console.error(
          'Invalid JSON response:',
          jsonError
        );


        return {

          success: false,

          message:
            'Invalid response from server',

        };

      }


      console.log(
        'Login API response:',
        response.status,
        data
      );


      /* ---------------------------------------------------
         HTTP Error
      --------------------------------------------------- */

      if (!response.ok) {

        return {

          success: false,

          message:
            data?.error ||
            data?.message ||
            `Login failed (${response.status})`,

        };

      }


      /* ---------------------------------------------------
         API Error
      --------------------------------------------------- */

      if (!data?.success) {

        return {

          success: false,

          message:
            data?.error ||
            data?.message ||
            'Invalid email or password',

        };

      }


      /* ---------------------------------------------------
         Check User
      --------------------------------------------------- */

      if (!data?.user) {

        return {

          success: false,

          message:
            'Login succeeded but user information was not returned',

        };

      }


      /* ---------------------------------------------------
         Check Token
      --------------------------------------------------- */

      if (!data?.token) {

        console.error(
          'Login succeeded but no token was returned:',
          data
        );


        return {

          success: false,

          message:
            'Login succeeded but authentication token was not returned',

        };

      }


      const authenticatedUser =
        data.user;


      const token =
        data.token;


      /* ---------------------------------------------------
         Save Authentication
      --------------------------------------------------- */

      const authData = {

        isAuthenticated: true,

        token: token,

        expiresAt:
          data.expiresAt ||
          null,

        user:
          authenticatedUser,

      };


      localStorage.setItem(
        AUTH_STORAGE_KEY,
        JSON.stringify(authData)
      );


      /* ---------------------------------------------------
         Update React State
      --------------------------------------------------- */

      setUser(
        authenticatedUser
      );


      console.log(
        'Authentication saved successfully.'
      );


      /* ---------------------------------------------------
         Success
      --------------------------------------------------- */

      return {

        success: true,

        user:
          authenticatedUser,

      };

    } catch (error) {

      console.error(
        'Login request failed:',
        error
      );


      return {

        success: false,

        message:
          error?.message ||
          'Unable to connect to login server',

      };

    } finally {

      setLoading(false);

    }

  };


  /* =======================================================
     Logout
  ======================================================= */

  const logout = async () => {
  try {
    const authData = JSON.parse(
      localStorage.getItem(AUTH_STORAGE_KEY) || 'null'
    )

    const token = authData?.token

    if (token) {
      try {
        await fetch(
          'https://www.my649.com/api/auth/logout.php',
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
            credentials: 'include',
          }
        )
      } catch (error) {
        console.warn('Logout API failed:', error)
      }
    }

  } finally {

    // Always clear local authentication
    localStorage.removeItem(AUTH_STORAGE_KEY)

    // Clear React authentication state
    setUser(null)
  }
}


  /* =======================================================
     Authentication State
  ======================================================= */

  const isAuthenticated =
    !!user;


  /* =======================================================
     Context Value
  ======================================================= */

  const value = {

    user,

    loading,

    login,

    logout,

    isAuthenticated,

  };


  return (

    <AuthContext.Provider
      value={value}
    >

      {children}

    </AuthContext.Provider>

  );

}


/* =========================================================
   useAuth
========================================================= */

export function useAuth() {

  const context =
    useContext(AuthContext);


  if (!context) {

    throw new Error(
      'useAuth() must be used inside <AuthProvider>'
    );

  }


  return context;

}